import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormTextField } from 'components/common';
import { ApplicationFormPageEnum } from '../types';
import { contactDetailsQuestions } from './questions';
import { IContactDetailsForm } from './types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { getCurrentRoute } from 'utils/location';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';
import { useNavigateBack } from 'hooks';
import { EMAIL_REGEXP } from '../../../common/regexp';
import { gbPhoneNumberRhfValidate } from 'utils/gbPhoneNumber';

export const ContactDetails = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IContactDetailsForm>({
    defaultValues: {
      email: null,
      phoneNumber: null,
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const isEmailUnique = (newEmail: string) => {
    if (currentRoute.includes('CONTACT_DETAILS&isJoined=true&activeApplicant=2')) {
      return !Object.entries(applicationInProgress as Record<string, string[]>)
        .filter((entry: any) => entry[0].includes('CONTACT_DETAILS&isJoined=true&activeApplicant=1'))[0][1]
        .map((question: any) => question.answer)
        .includes(newEmail);
    }
    return true;
  };

  const submitFormHandler = async (formData: IContactDetailsForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: contactDetailsQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.RESIDENCY);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={contactDetailsQuestions.email.labelKey} tagName={'h4'} />

          <FormTextField
            name={'email'}
            rules={{
              required: requiredFieldErrorMessage,
              pattern: {
                value: EMAIL_REGEXP,
                message: intl.formatMessage({ id: 'Must be a valid email address' }),
              },
              validate: value => {
                const isUnique = isEmailUnique(value);
                return isUnique || intl.formatMessage({ id: 'Email must be different for each applicant.' });
              },
            }}
          />
        </section>

        <section>
          <FormattedMessage id={contactDetailsQuestions.phoneNumber.labelKey} tagName={'h4'} />

          <FormTextField
            name={'phoneNumber'}
            rules={{
              required: requiredFieldErrorMessage,
              validate: gbPhoneNumberRhfValidate(intl.formatMessage({ id: 'common.error.validPhoneNumber' })),
            }}
          />
        </section>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
